'use client';

import { useEffect } from 'react';
import { getRollbar } from '@/services/rollbar';

// Hosts for our CSP allow-listed third-party scripts (Piwik PRO, Intercom,
// cdnjs). Used below to tag which "bucket" an eval violation's sourceFile
// falls into, purely for filtering/analysis in Rollbar — nothing is dropped.
const ALLOWED_THIRD_PARTY_SCRIPT_HOSTS = ['piwik.pro', 'intercomcdn.com', 'intercom.io', 'cdnjs.cloudflare.com'];

// Browser-injected page-translation tools (Firefox's built-in translator, the
// Google Translate extension) load their own script/WASM and make their own
// network/image requests directly against whatever page the visitor is
// translating — none of that is under our control, so it trips our CSP as if
// it were our own code. These are the two URLs Google Translate's client
// itself requests; matching them lets us tag connect-src/img-src violations
// with certainty (unlike the eval/wasm-eval case below, which can only be
// inferred from correlation in aggregate, not proven per individual event).
const GOOGLE_TRANSLATE_URL_PATTERNS = ['translate.google.com', 'fonts.gstatic.com/s/i/productlogos/translate'];

type EvalSource = 'own-bundle' | 'allowed-third-party' | 'other';

/**
 * Initializes the global Rollbar browser client on mount. Rendered once in the
 * root layout so it runs on every page, enabling captureUncaught /
 * captureUnhandledRejections and the CSP autoInstrument listener. Renders nothing.
 */
export function RollbarInit() {
  useEffect(() => {
    const rollbar = getRollbar();

    // Report each distinct violation once per page load. A single pageview can
    // trigger dozens of identical violations in seconds (e.g. Firefox's
    // translator calling eval repeatedly), which would otherwise become dozens
    // of Rollbar occurrences. Deduping keeps the count meaningful: occurrences
    // ~ sessions affected, not eval calls.
    const reported = new Set<string>();

    // Report CSP violations ourselves so the item includes `sample` (first ~40
    // chars of the offending code, requires 'report-sample' in the directive)
    // and `sourceFile` — both omitted by Rollbar's built-in CSP autoInstrument.
    const onViolation = (event: SecurityPolicyViolationEvent) => {
      const key = [
        event.violatedDirective,
        event.blockedURI,
        event.sourceFile,
        event.lineNumber,
        event.columnNumber,
      ].join('|');
      if (reported.has(key)) return;
      reported.add(key);

      // INVESTIGATION MODE started 2026-08-13, ahead of switching CSP from
      // report-only to enforcing: we report every violation (nothing dropped
      // client-side) with enough detail — `sample`, `sourceFile`, `evalSource`
      // — to identify the real source, because an earlier guess that most of
      // this was Google Translate / browser extensions was never actually
      // confirmed with real data (the historical ~1000-item flood we'd based
      // that on predated this listener entirely — it came from Rollbar's
      // built-in CSP autoInstrument, which doesn't capture sourceFile or
      // sample at all; see services/rollbar.ts).
      //
      // RESOLVED 2026-08-21: production volume analysis confirmed it. The
      // eval/wasm-eval cluster is ~98% Firefox 149-153 (the versions that
      // shipped Firefox's on-device page translation), and it correlates in
      // the same sessions with connect-src/img-src hits on Google Translate's
      // own beacon (translate.google.com/gen204) and icon
      // (fonts.gstatic.com/.../translate/...). Nothing in our own bundle calls
      // eval, and we don't need script-src eval or wasm-eval for anything we
      // ship, so this is safe to enforce against.
      //
      // We only drop exact repeats within one page load (see `reported` above);
      // distinct violations are all still sent. Mute/snooze the resulting
      // Rollbar item to kill notification noise. Grouping: the
      // message includes the violated directive PLUS the bucket tag
      // (evalSource / google-translate) below. Rollbar groups message-based
      // items by the message string itself — custom data fields alone don't
      // split occurrences into separate items, they're only visible once you
      // open one. Folding the tag into the message is what actually gets you
      // distinct, scannable items in the list view (e.g. "CSP violation:
      // script-src (eval: other)" vs "CSP violation: connect-src
      // (google-translate)") instead of one lumped "CSP violation: script-src"
      // bucket you have to click into to tell apart.
      const isEval = event.blockedURI === 'eval' || event.blockedURI === 'wasm-eval';
      const isOwnBundle = event.sourceFile?.includes('/_next/') ?? false;
      const isAllowedThirdParty = ALLOWED_THIRD_PARTY_SCRIPT_HOSTS.some((host) => event.sourceFile?.includes(host));
      const evalSource: EvalSource = isOwnBundle ? 'own-bundle' : isAllowedThirdParty ? 'allowed-third-party' : 'other';
      const isGoogleTranslateUrl = GOOGLE_TRANSLATE_URL_PATTERNS.some((pattern) => event.blockedURI?.includes(pattern));

      const tag = isEval ? `eval: ${evalSource}` : isGoogleTranslateUrl ? 'google-translate' : undefined;
      const message = `CSP violation: ${event.violatedDirective}${tag ? ` (${tag})` : ''}`;

      rollbar?.error(message, {
        blockedURI: event.blockedURI,
        violatedDirective: event.violatedDirective,
        effectiveDirective: event.effectiveDirective,
        disposition: event.disposition,
        sourceFile: event.sourceFile,
        lineNumber: event.lineNumber,
        columnNumber: event.columnNumber,
        sample: event.sample,
        ...(isEval ? { evalSource } : {}),
        ...(isGoogleTranslateUrl ? { likelySource: 'google-translate' } : {}),
      });
    };

    document.addEventListener('securitypolicyviolation', onViolation);
    return () => document.removeEventListener('securitypolicyviolation', onViolation);
  }, []);

  return null;
}
